import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowSquareOut, CaretDown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DateField } from "@/components/DateField";
import { ReceivePaymentDialog } from "@/components/ProjectDealActions";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/lib/poFormat";

const day = (iso) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const STATUS_TONE = {
  Paid: "border-emerald-500/40 text-emerald-300",
  Received: "border-emerald-500/40 text-emerald-300",
  "Partly paid": "border-amber-500/40 text-amber-300",
  Overdue: "border-red-500/40 text-red-300",
  Void: "border-zinc-700 text-zinc-500",
  Cancelled: "border-zinc-700 text-zinc-500",
  Deposited: "border-sky-500/40 text-sky-300",
};

const Pill = ({ s }) => (
  <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${STATUS_TONE[s] || "border-zinc-700 text-zinc-300"}`}>
    {s}
  </span>
);

const Section = ({ title, action, children, testId }) => (
  <section data-testid={testId} className="space-y-2">
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{title}</h3>
      {action}
    </div>
    <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900">{children}</div>
  </section>
);

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 ${right ? "text-right" : "text-left"}`}>{children}</th>
);

const DocTable = ({ rows, href, empty, testId }) => (
  <table className="w-full text-sm" data-testid={testId}>
    <thead className="border-b border-zinc-800">
      <tr>
        <Th>FinDoc &amp; Description</Th>
        <Th>Counterparty</Th>
        <Th>Issued</Th>
        <Th>Due</Th>
        <Th right>Amount</Th>
        <Th right>Balance due</Th>
        <Th>Other info</Th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && (
        <tr>
          <td colSpan={7} className="px-3 py-6 text-center text-xs text-zinc-500">{empty}</td>
        </tr>
      )}
      {rows.map((r) => {
        const overdue = r.balance > 0.009 && r.due && r.due < new Date().toISOString().slice(0, 10) && !["Void", "Cancelled"].includes(r.status);
        return (
          <tr key={r.id} data-testid={`${testId}-row-${r.id}`} className="border-b border-zinc-800/60 last:border-0 hover:bg-zinc-800/40">
            <td className="px-3 py-2">
              {href(r) ? (
                <Link to={href(r)} className="font-mono-data text-xs font-semibold text-indigo-300 hover:underline">{r.code}</Link>
              ) : (
                <span className="font-mono-data text-xs font-semibold text-zinc-300">{r.code}</span>
              )}
              <div className="truncate text-xs text-zinc-400">{r.description}</div>
            </td>
            <td className="px-3 py-2 text-xs text-zinc-300">{r.counterparty || "—"}</td>
            <td className="px-3 py-2 text-xs text-zinc-400">{day(r.issued)}</td>
            <td className={`px-3 py-2 text-xs ${overdue ? "text-red-300" : "text-zinc-400"}`}>{day(r.due)}</td>
            <td className="px-3 py-2 text-right font-mono-data text-xs">{fmtMoney(r.amount)}</td>
            <td className={`px-3 py-2 text-right font-mono-data text-xs ${r.balance > 0.009 ? "text-amber-300" : "text-zinc-500"}`}>{fmtMoney(r.balance)}</td>
            <td className="px-3 py-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Pill s={overdue ? "Overdue" : r.status} />
                {r.info && <span className="text-[10px] text-zinc-500">{r.info}</span>}
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
    <tfoot className="border-t border-zinc-800 bg-zinc-950/60">
      <tr>
        <td colSpan={4} className="px-3 py-2 text-xs text-zinc-400">Total: {rows.length} document{rows.length === 1 ? "" : "s"}</td>
        <td className="px-3 py-2 text-right font-mono-data text-xs font-bold">{fmtMoney(rows.reduce((s, r) => s + (["Void", "Cancelled"].includes(r.status) ? 0 : r.amount), 0))}</td>
        <td className="px-3 py-2 text-right font-mono-data text-xs font-bold">{fmtMoney(rows.reduce((s, r) => s + (["Void", "Cancelled"].includes(r.status) ? 0 : r.balance), 0))}</td>
        <td />
      </tr>
    </tfoot>
  </table>
);

const PaymentsTable = ({ rows }) => (
  <table className="w-full text-sm" data-testid="finance-payments-table">
    <thead className="border-b border-zinc-800">
      <tr>
        <Th>Rcpt date</Th>
        <Th>Counterparty</Th>
        <Th>Method</Th>
        <Th>Ref #</Th>
        <Th right>Amount</Th>
        <Th>Status</Th>
        <Th>Applied to</Th>
        <Th>Memo</Th>
        <Th />
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && (
        <tr>
          <td colSpan={9} className="px-3 py-6 text-center text-xs text-zinc-500">No payments received yet</td>
        </tr>
      )}
      {rows.map((p) => (
        <tr key={`${p.invoice_id}-${p.id}`} data-testid={`finance-payment-${p.id}`} className="border-b border-zinc-800/60 last:border-0 hover:bg-zinc-800/40">
          <td className="px-3 py-2 text-xs text-zinc-300">{day(p.at)}</td>
          <td className="px-3 py-2 text-xs text-zinc-300">{p.counterparty || "—"}</td>
          <td className="px-3 py-2 text-xs text-zinc-400">{p.method || "—"}</td>
          <td className="px-3 py-2 font-mono-data text-xs text-zinc-400">{p.reference || "—"}</td>
          <td className="px-3 py-2 text-right">
            <div className="font-mono-data text-xs font-bold text-emerald-300">+{fmtMoney(p.amount)}</div>
            <div className="text-[10px] text-zinc-500">Left: {fmtMoney(p.left)}</div>
          </td>
          <td className="px-3 py-2"><Pill s={p.status} /></td>
          <td className="px-3 py-2">
            <Link to={`/invoices/${p.invoice_id}`} className="font-mono-data text-xs text-indigo-300 hover:underline">{p.invoice_code}</Link>
          </td>
          <td className="max-w-[16rem] truncate px-3 py-2 text-xs text-zinc-400">{p.note || "—"}</td>
          <td className="px-3 py-2 text-right">
            <Link to={`/invoices/${p.invoice_id}`} className="inline-flex text-zinc-500 hover:text-indigo-300" aria-label="Open invoice">
              <ArrowSquareOut size={14} weight="bold" />
            </Link>
          </td>
        </tr>
      ))}
    </tbody>
    <tfoot className="border-t border-zinc-800 bg-zinc-950/60">
      <tr>
        <td colSpan={4} className="px-3 py-2 text-xs text-zinc-400">Total: {rows.length} payment{rows.length === 1 ? "" : "s"}</td>
        <td className="px-3 py-2 text-right font-mono-data text-xs font-bold" data-testid="finance-payments-total">{fmtMoney(rows.reduce((s, p) => s + p.amount, 0))}</td>
        <td colSpan={4} />
      </tr>
    </tfoot>
  </table>
);

const RECV_TYPES = {
  deposit: { label: "Request Deposit", title: "Deposit", credit: false },
  invoice: { label: "Issue Invoice", title: "Invoice", credit: false },
  credit_memo: { label: "Issue Credit Memo", title: "Credit memo", credit: true },
  refund: { label: "Issue Refund", title: "Refund", credit: true },
};

const BILL_TYPES = {
  bill: { label: "Receive Bill" },
  vendor_credit: { label: "Vendor Credit Memo" },
};

const IssueInvoiceDialog = ({ open, onOpenChange, customerId, projectId, kind = "invoice", onDone }) => {
  const info = RECV_TYPES[kind] || RECV_TYPES.invoice;
  const [f, setF] = useState({ title: info.title, amount: "", due_date: "", po_number: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF({ title: info.title, amount: "", due_date: "", po_number: "", notes: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);
  const submit = async () => {
    const amount = Number(f.amount);
    if (!(amount > 0)) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", { ...f, kind, amount, customer_id: customerId || "", project_id: projectId || "" });
      toast.success(`${data.code} ${info.credit ? "credit created" : "issued"}`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create document");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="issue-invoice-dialog" className="border-zinc-800 bg-zinc-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">{info.label}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {info.credit
              ? "Creates a customer credit — it reduces the balance they owe and can be applied to an open invoice."
              : `Creates a draft ${info.title.toLowerCase()} on this ${projectId ? "project" : "account"}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Description
            <Input data-testid="issue-invoice-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Amount
              <Input data-testid="issue-invoice-amount" type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
            </label>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Due
              <DateField testid="issue-invoice-due" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} className="mt-1" placeholder="Default terms" />
            </label>
          </div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Customer PO #
            <Input data-testid="issue-invoice-po" value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
          </label>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Notes
            <Textarea data-testid="issue-invoice-notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
          <Button data-testid="issue-invoice-save" disabled={busy} onClick={submit} className="bg-indigo-500 hover:bg-indigo-400">{info.credit ? "Create" : "Issue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BillDialog = ({ open, onOpenChange, customerId, projectId, kind = "bill", onDone }) => {
  const info = BILL_TYPES[kind] || BILL_TYPES.bill;
  const [f, setF] = useState({ supplier_name: "", title: info.label, amount: "", due_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF({ supplier_name: "", title: info.label, amount: "", due_date: "", notes: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);
  const submit = async () => {
    const amount = Number(f.amount);
    if (!(amount > 0)) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const { data } = await api.post("/payables/bill", { ...f, kind, amount, customer_id: customerId || "", project_id: projectId || "" });
      toast.success(`${data.code} created`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create bill");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bill-dialog" className="border-zinc-800 bg-zinc-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">{info.label}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {kind === "vendor_credit" ? "Records a credit from a supplier — it reduces what you owe them." : "Records a bill you owe a supplier."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Supplier
            <Input data-testid="bill-supplier" value={f.supplier_name} onChange={(e) => setF({ ...f, supplier_name: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
          </label>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Description
            <Input data-testid="bill-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Amount
              <Input data-testid="bill-amount" type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
            </label>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Due
              <DateField testid="bill-due" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} className="mt-1" placeholder="Optional" />
            </label>
          </div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Notes
            <Textarea data-testid="bill-notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
          <Button data-testid="bill-save" disabled={busy} onClick={submit} className="bg-indigo-500 hover:bg-indigo-400">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const IssueBtn = ({ onClick, testId, label = "Issue" }) => (
  <Button data-testid={testId} size="sm" variant="outline" onClick={onClick} className="h-8 border-indigo-500/50 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/10">
    <Plus size={13} weight="bold" className="mr-1" /> {label}
  </Button>
);

const PayField = ({ label, children }) => (
  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
    {label}
    {children}
  </label>
);

const BulkPaymentDialog = ({ open, onOpenChange, invoices, onDone }) => {
  const openInv = [...invoices]
    .filter((i) => i.balance > 0.009 && !["Void", "Paid", "Cancelled"].includes(i.status))
    .sort((a, b) => (a.issued || "").localeCompare(b.issued || ""));
  const [alloc, setAlloc] = useState({});
  const [meta, setMeta] = useState({ method: "Check", reference: "", note: "", at: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setAlloc(Object.fromEntries(openInv.map((i) => [i.id, i.balance.toFixed(2)])));
    setMeta({ method: "Check", reference: "", note: "", at: new Date().toISOString().slice(0, 10) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const total = openInv.reduce((s, i) => s + (Number(alloc[i.id]) || 0), 0);
  const distribute = (val) => {
    let left = Math.round((Number(val) || 0) * 100) / 100;
    const next = {};
    for (const i of openInv) {
      const take = Math.max(Math.min(left, i.balance), 0);
      next[i.id] = take > 0.009 ? take.toFixed(2) : "";
      left = Math.round((left - take) * 100) / 100;
    }
    setAlloc(next);
  };
  const submit = async () => {
    const allocations = openInv
      .map((i) => ({ invoice_id: i.id, amount: Number(alloc[i.id]) || 0 }))
      .filter((a) => a.amount > 0);
    if (!allocations.length) {
      toast.error("Enter at least one amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/bulk-payment", {
        allocations,
        method: meta.method,
        reference: meta.reference,
        note: meta.note,
        at: meta.at ? new Date(`${meta.at}T12:00:00`).toISOString() : "",
      });
      toast.success(`Applied ${fmtMoney(data.applied)} across ${data.count} invoice${data.count === 1 ? "" : "s"}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply payment");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-payment-dialog" className="border-zinc-800 bg-zinc-900 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">Receive payment</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Apply one payment across {openInv.length} open invoice{openInv.length === 1 ? "" : "s"}. Amounts default to each balance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Distribute total</span>
            <Input
              data-testid="bulk-distribute-input"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              className="h-8 w-32 border-zinc-800 bg-zinc-900 text-right font-mono-data text-xs"
              onChange={(e) => distribute(e.target.value)}
            />
            <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">oldest first</span>
          </div>
          <div className="max-h-64 space-y-1.5 overflow-y-auto">
            {openInv.length === 0 && <div className="py-4 text-center text-xs text-zinc-500">No open invoices</div>}
            {openInv.map((i) => (
              <div key={i.id} data-testid={`bulk-row-${i.id}`} className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="font-mono-data text-xs font-semibold text-indigo-300">{i.code}</div>
                  <div className="truncate text-[11px] text-zinc-500">{i.description || "Invoice"} · bal {fmtMoney(i.balance)}</div>
                </div>
                <Input
                  data-testid={`bulk-amount-${i.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={alloc[i.id] ?? ""}
                  onChange={(e) => setAlloc((a) => ({ ...a, [i.id]: e.target.value }))}
                  className="h-8 w-28 border-zinc-800 bg-zinc-900 text-right font-mono-data text-xs"
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <PayField label="Method">
              <Input data-testid="bulk-method" value={meta.method} onChange={(e) => setMeta({ ...meta, method: e.target.value })} className="mt-1 h-8 border-zinc-800 bg-zinc-950 text-xs" />
            </PayField>
            <PayField label="Ref #">
              <Input data-testid="bulk-reference" value={meta.reference} onChange={(e) => setMeta({ ...meta, reference: e.target.value })} className="mt-1 h-8 border-zinc-800 bg-zinc-950 text-xs" />
            </PayField>
            <PayField label="Date">
              <DateField testid="bulk-date" value={meta.at} onChange={(v) => setMeta({ ...meta, at: v })} className="mt-1" />
            </PayField>
          </div>
        </div>
        <DialogFooter className="items-center">
          <span className="mr-auto text-xs text-zinc-400">
            Total: <span data-testid="bulk-total" className="font-mono-data font-bold text-emerald-300">{fmtMoney(total)}</span>
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
          <Button data-testid="bulk-apply" disabled={busy || total <= 0} onClick={submit} className="bg-emerald-500 hover:bg-emerald-400">Apply payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ApplyCreditDialog = ({ open, onOpenChange, credits, targets, onDone }) => {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const from = credits.find((c) => c.id === fromId);
  const to = targets.find((t) => t.id === toId);
  const max = Math.min(from ? -from.balance : 0, to ? to.balance : 0);
  useEffect(() => {
    if (!open) return;
    const f = credits[0];
    const t = targets[0];
    setFromId(f ? f.id : "");
    setToId(t ? t.id : "");
    const amt = f && t ? Math.min(-f.balance, t.balance) : 0;
    setAmount(amt > 0 ? amt.toFixed(2) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) {
      toast.error("Enter an amount");
      return;
    }
    if (!fromId || !toId) {
      toast.error("Pick both invoices");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/credit-transfer", { from_invoice_id: fromId, to_invoice_id: toId, amount: amt });
      toast.success(`Applied ${fmtMoney(data.applied)} credit`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply credit");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="apply-credit-dialog" className="border-zinc-800 bg-zinc-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">Apply credit</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Move an over-payment from one invoice onto another open invoice.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <PayField label="Credit from">
            <select data-testid="credit-from" value={fromId} onChange={(e) => setFromId(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-200">
              {credits.map((c) => (
                <option key={c.id} value={c.id}>{c.code} · credit {fmtMoney(-c.balance)}</option>
              ))}
            </select>
          </PayField>
          <PayField label="Apply to">
            <select data-testid="credit-to" value={toId} onChange={(e) => setToId(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-200">
              {targets.map((t) => (
                <option key={t.id} value={t.id}>{t.code} · owing {fmtMoney(t.balance)}</option>
              ))}
            </select>
          </PayField>
          <PayField label={`Amount (max ${fmtMoney(max)})`}>
            <Input data-testid="credit-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-9 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs" />
          </PayField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
          <Button data-testid="credit-apply" disabled={busy || !(Number(amount) > 0) || !fromId || !toId} onClick={submit} className="bg-emerald-500 hover:bg-emerald-400">Apply credit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PayBillDialog = ({ open, onOpenChange, bills, onDone }) => {
  const openBills = bills.filter((b) => b.is_bill && b.balance > 0.009);
  const [billId, setBillId] = useState("");
  const [meta, setMeta] = useState({ amount: "", method: "Check", reference: "", note: "", at: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const b = openBills[0];
    setBillId(b ? b.id : "");
    setMeta({ amount: b ? b.balance.toFixed(2) : "", method: "Check", reference: "", note: "", at: new Date().toISOString().slice(0, 10) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = async () => {
    const amount = Number(meta.amount);
    if (!(amount > 0)) return toast.error("Enter an amount");
    if (!billId) return toast.error("Pick a bill");
    setBusy(true);
    try {
      await api.post(`/payables/bill/${billId}/payments`, { amount, method: meta.method, reference: meta.reference, note: meta.note, at: meta.at ? new Date(`${meta.at}T12:00:00`).toISOString() : "" });
      toast.success(`Paid ${fmtMoney(amount)}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not record payment");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pay-bill-dialog" className="border-zinc-800 bg-zinc-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">Pay bill</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Record money paid to a supplier against an open bill.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <PayField label="Bill">
            <select data-testid="pay-bill-select" value={billId} onChange={(e) => { const b = openBills.find((x) => x.id === e.target.value); setBillId(e.target.value); setMeta((m) => ({ ...m, amount: b ? b.balance.toFixed(2) : "" })); }} className="mt-1 h-9 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-200">
              {openBills.map((b) => (<option key={b.id} value={b.id}>{b.code} · {b.counterparty || "Supplier"} · owing {fmtMoney(b.balance)}</option>))}
            </select>
          </PayField>
          <div className="grid grid-cols-3 gap-2">
            <PayField label="Amount"><Input data-testid="pay-bill-amount" type="number" min="0" step="0.01" value={meta.amount} onChange={(e) => setMeta({ ...meta, amount: e.target.value })} className="mt-1 h-8 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs" /></PayField>
            <PayField label="Method"><Input data-testid="pay-bill-method" value={meta.method} onChange={(e) => setMeta({ ...meta, method: e.target.value })} className="mt-1 h-8 border-zinc-800 bg-zinc-950 text-xs" /></PayField>
            <PayField label="Date"><DateField testid="pay-bill-date" value={meta.at} onChange={(v) => setMeta({ ...meta, at: v })} className="mt-1" /></PayField>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700">Cancel</Button>
          <Button data-testid="pay-bill-apply" disabled={busy || !(Number(meta.amount) > 0) || !billId} onClick={submit} className="bg-emerald-500 hover:bg-emerald-400">Record payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FinanceLedger = ({ scope, id, project, customerId, onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [recv, setRecv] = useState(null);
  const [billKind, setBillKind] = useState(null);
  const [pay, setPay] = useState(false);
  const [payBill, setPayBill] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/${scope}/${id}/finances`);
      setData(d);
    } catch {
      toast.error("Could not load finances");
    }
  }, [scope, id]);
  useEffect(() => {
    load();
  }, [load]);
  const refresh = () => {
    load();
    onChanged?.();
  };
  if (!data)
    return <div data-testid="finance-loading" className="py-8 text-center text-xs uppercase tracking-widest text-zinc-500">Loading finances…</div>;
  const t = data.totals;
  const cid = customerId || project?.customer_id || "";
  const poHref = project ? `/inventory/purchase-orders/new?project=${project.id}&customer=${cid}` : `/inventory/purchase-orders/new?customer=${cid}`;
  const invoicesForPay = data.receivables.map((r) => ({ id: r.id, code: r.code, status: r.status, balance: r.balance }));
  const openReceivables = data.receivables.filter((r) => r.balance > 0.009 && !["Void", "Cancelled"].includes(r.status));
  const credits = data.receivables.filter((r) => r.balance < -0.009 && !["Void", "Cancelled"].includes(r.status));
  const openBills = data.payables.filter((r) => r.is_bill && r.balance > 0.009);
  return (
    <div className="space-y-6" data-testid="finance-ledger">
      <div className="grid gap-3 sm:grid-cols-4" data-testid="finance-totals">
        {[
          ["Receivable", t.receivable_amount, "text-zinc-100"],
          ["Open balance", t.receivable_balance, t.receivable_balance > 0 ? "text-amber-300" : "text-zinc-300"],
          ["Payable", t.payable_amount, "text-zinc-100"],
          ["Payments in", t.payments, "text-emerald-300"],
        ].map(([l, v, c]) => (
          <div key={l} className="rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className={`mt-0.5 font-mono-data text-lg font-bold ${c}`}>{fmtMoney(v)}</div>
          </div>
        ))}
      </div>
      <Section
        title="Receivables"
        testId="finance-receivables"
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="finance-issue-receivable" size="sm" variant="outline" className="h-8 border-indigo-500/50 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/10">
                <Plus size={13} weight="bold" className="mr-1" /> Issue <CaretDown size={11} weight="bold" className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-zinc-800 bg-zinc-900">
              {Object.entries(RECV_TYPES).map(([k, v]) => (
                <DropdownMenuItem key={k} data-testid={`finance-recv-${k}`} onSelect={() => setRecv(k)} className="text-xs text-zinc-200 focus:bg-zinc-800 focus:text-white">{v.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        <DocTable rows={data.receivables} href={(r) => `/invoices/${r.id}`} empty="No financial documents" testId="finance-receivables-table" />
      </Section>
      <Section
        title="Payables"
        testId="finance-payables"
        action={
          <div className="flex items-center gap-2">
            {openBills.length > 0 && (
              <IssueBtn testId="finance-pay-bill" label="Pay bill" onClick={() => setPayBill(true)} />
            )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="finance-issue-payable" size="sm" variant="outline" className="h-8 border-indigo-500/50 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/10">
                <Plus size={13} weight="bold" className="mr-1" /> Issue <CaretDown size={11} weight="bold" className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-zinc-800 bg-zinc-900">
              {Object.entries(BILL_TYPES).map(([k, v]) => (
                <DropdownMenuItem key={k} data-testid={`finance-bill-${k}`} onSelect={() => setBillKind(k)} className="text-xs text-zinc-200 focus:bg-zinc-800 focus:text-white">{v.label}</DropdownMenuItem>
              ))}
              <DropdownMenuItem data-testid="finance-issue-po" onSelect={() => navigate(poHref)} className="text-xs text-zinc-200 focus:bg-zinc-800 focus:text-white">New purchase order</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        }
      >
        <DocTable rows={data.payables} href={(r) => (r.is_bill ? `/payables/bills/${r.id}` : `/inventory/purchase-orders/${r.id}`)} empty="No financial documents" testId="finance-payables-table" />
      </Section>
      <Section
        title="Payments"
        testId="finance-payments"
        action={
          <div className="flex flex-wrap items-center gap-2">
            {credits.length > 0 && (
              <IssueBtn testId="finance-apply-credit" label="Apply credit" onClick={() => setCreditOpen(true)} />
            )}
            {!project && openReceivables.length > 0 && (
              <IssueBtn testId="finance-receive-bulk" label="Receive payment" onClick={() => setBulk(true)} />
            )}
            {project && openReceivables.length > 1 && (
              <IssueBtn testId="finance-split-payment" label="Split payment" onClick={() => setBulk(true)} />
            )}
            {project && (
              <IssueBtn testId="finance-receive-payment" label="Receive payment" onClick={() => setPay(true)} />
            )}
          </div>
        }
      >
        <PaymentsTable rows={data.payments} />
      </Section>
      <IssueInvoiceDialog open={!!recv} onOpenChange={(v) => !v && setRecv(null)} kind={recv || "invoice"} customerId={cid} projectId={project?.id || ""} onDone={refresh} />
      <BillDialog open={!!billKind} onOpenChange={(v) => !v && setBillKind(null)} kind={billKind || "bill"} customerId={cid} projectId={project?.id || ""} onDone={refresh} />
      <PayBillDialog open={payBill} onOpenChange={setPayBill} bills={data.payables} onDone={refresh} />
      <BulkPaymentDialog open={bulk} onOpenChange={setBulk} invoices={data.receivables} onDone={refresh} />
      <ApplyCreditDialog open={creditOpen} onOpenChange={setCreditOpen} credits={credits} targets={openReceivables} onDone={refresh} />
      {project && (
        <ReceivePaymentDialog open={pay} onClose={() => setPay(false)} project={project} invoices={invoicesForPay} onDone={refresh} />
      )}
    </div>
  );
};
